import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { ComparisonTableData } from '../../shared/enums';
import { SelectedRow } from '../../shared/models';
import { ProductsService } from '../../shared/services/products.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-comparison-table',
  templateUrl: './comparison.component.html',
  styleUrls: ['./comparison.component.scss'],
  standalone: false,
})
export class ComparisonComponent implements OnInit, OnDestroy {
  private selectedCheckboxesSubscription: Subscription;
  selectedModels: SelectedRow[] = [];
  comparisonData: ComparisonTableData[] = [];
  units = {
    maxFlow: 'm³/h',
    netWeight: 'kg',
    height: 'mm',
    depth: 'mm',
    width: 'mm',
    airConnection: 'Rp',
    nominalConsumption: 'kW',
    nominalCurrent: 'A',
    fullLoadCurrent: 'A',
    lockedRotorCurrent: 'A',
  };

  constructor(
    private productService: ProductsService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.selectedCheckboxesSubscription = this.productService.selectedCheckboxes$.subscribe((selectedModels) => {
      this.selectedModels = selectedModels || [];
      this.constructComparisonData();
    });
  }

  ngOnDestroy(): void {
    this.selectedCheckboxesSubscription?.unsubscribe();
  }

  constructComparisonData(): void {
    // Clear existing comparison data first
    this.comparisonData = [];

    // Only proceed if there are at least 2 selected models
    if (!this.selectedModels || this.selectedModels.length < 2) {
      return;
    }

    const attributes = [
      'model',
      'maxFlow',
      'netWeight',
      'height',
      'depth',
      'width',
      'airConnection',
      'coolingMedia',
      'nominalConsumption',
      'nominalCurrent',
      'fullLoadCurrent',
      'lockedRotorCurrent',
    ];

    const titles = {
      model: 'MODEL',
      maxFlow: 'MAX_FLOW',
      netWeight: 'NET_WEIGHT_KG',
      height: 'FIELDS.HEIGHT',
      depth: 'FIELDS.DEPTH',
      width: 'FIELDS.WIDTH',
      airConnection: 'FIELDS.AIR_CONNECTIONS',
      coolingMedia: 'FIELDS.COOLING_MEDIA',
      nominalConsumption: 'FIELDS.NOMINAL_CONSUMPTION',
      nominalCurrent: 'FIELDS.NOMINAL_CURRENT',
      fullLoadCurrent: 'FIELDS.FULL_LOAD_CURRENT',
      lockedRotorCurrent: 'FIELDS.LOCKED_ROTOR_CURRENT',
    };

    attributes.forEach((attr) => {
      const comparisonRow: any = { title: titles[attr as keyof typeof titles] };
      this?.selectedModels.forEach((model, index) => {
        const modelData = model?.exactModel || model;
        const value = modelData[attr];
        const unit = this.units[attr as keyof typeof this.units] || '';
        comparisonRow[`model${index + 1}`] = value != null ? `${value} ${unit}` : '-';
      });
      this.comparisonData.push(comparisonRow);
      if (attr === 'model') {
        const percentageRow: any = { title: 'EXTRA_AIR_FLOW_AVAILABLE' };
        this.selectedModels.forEach((model, index) => {
          const percentage = model.percentage;
          percentageRow[`model${index + 1}`] = percentage != null ? ` ${percentage}%` : '-';
        });
        this.comparisonData.push(percentageRow);
      }
    });
  }

  isOdd(index: number): boolean {
    return index % 2 === 1;
  }

  isEven(index: number): boolean {
    return index % 2 === 0;
  }
}
